import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DiffEntry, ImportPreview } from '@msc-stock/shared';
import { api } from '../lib/api.js';
import { useI18n, yen } from '../i18n.js';
import { Badge, Card, DivisionBadge, Empty, Field, Notice, Tabs } from '../components/ui.js';

type Tab = 'added' | 'sold' | 'returned' | 'updated';

/**
 * 在庫リストの取り込み。
 *
 * かならず 2 段階にしてある。まずファイルを送って差分だけを見せ、
 * 内容 (とくに「売却」になる台数) を確認してから確定する。在庫リストから
 * 消えた車は自動で売却になるので、うっかり古いファイルを入れたときに
 * 気付けるようにするための作りになっている。
 */
export function ImportPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [filename, setFilename] = useState('');
  const [snapshotDate, setSnapshotDate] = useState('');
  const [note, setNote] = useState('');
  const [tab, setTab] = useState<Tab>('added');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ added: number; sold: number; returned: number; updated: number } | null>(null);

  const entriesByKind = useMemo(() => {
    const map: Record<Tab, DiffEntry[]> = { added: [], sold: [], returned: [], updated: [] };
    for (const e of preview?.entries ?? []) {
      if (e.kind in map) map[e.kind as Tab].push(e);
    }
    return map;
  }, [preview]);

  async function runPreview() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.previewImport(file);
      setPreview(r.preview);
      setFilename(r.filename);
      setSnapshotDate(r.preview.snapshotDate ?? '');
      // 最初に開くタブは、いちばん確認してほしいものにする
      const counts = r.preview.sheets.reduce(
        (a, s) => ({ added: a.added + s.added, sold: a.sold + s.sold }),
        { added: 0, sold: 0 },
      );
      setTab(counts.sold > 0 ? 'sold' : counts.added > 0 ? 'added' : 'updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.commitImport({
        stagingKey: preview.stagingKey,
        filename,
        snapshotDate: snapshotDate || null,
        note: note.trim() || null,
      });
      setDone(r.result);
      setPreview(null);
      setFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const totals = preview?.sheets.reduce(
    (a, s) => ({
      added: a.added + s.added,
      sold: a.sold + s.sold,
      returned: a.returned + s.returned,
      updated: a.updated + s.updated,
      unchanged: a.unchanged + s.unchanged,
    }),
    { added: 0, sold: 0, returned: 0, updated: 0, unchanged: 0 },
  );

  return (
    <>
      <h1 className="page-title">{t('uploadTitle')}</h1>
      <p className="page-sub">{t('uploadHint')}</p>

      {error && <Notice kind="bad">{error}</Notice>}

      {done && (
        <Notice kind="ok">
          {t('added')} {done.added} · {t('soldDiff')} {done.sold} · {t('returned')} {done.returned} ·{' '}
          {t('updated')} {done.updated}{' '}
          <button type="button" className="btn small" onClick={() => navigate('/imports')}>
            {t('history')} →
          </button>
        </Notice>
      )}

      <Card>
        <div className="row">
          <input
            type="file"
            accept=".xlsx,.xls"
            style={{ width: 'auto' }}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
              setDone(null);
            }}
          />
          <button type="button" className="btn primary" disabled={!file || busy} onClick={runPreview}>
            {busy && !preview ? t('loading') : t('checkDiff')}
          </button>
        </div>
      </Card>

      {preview && (
        <>
          {preview.warnings.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <Notice kind="warn">
                <strong>{t('warnings')}</strong>
                <ul>
                  {preview.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </Notice>
            </div>
          )}

          <div className="grid cols-2" style={{ marginTop: 14 }}>
            {preview.sheets.map((s) => (
              <Card
                key={s.division}
                title={
                  <>
                    <DivisionBadge division={s.division} /> {s.parsedCount} {t('vehicles')} ·{' '}
                    {yen(s.parsedSum)}
                  </>
                }
              >
                {/* Excel 自身が持っている合計行と突き合わせた結果。
                    ここが一致していれば、読み飛ばしや重複取り込みは起きていない。 */}
                <p style={{ marginTop: 0 }}>
                  {s.totalsMatch ? (
                    <Badge kind="ok">{t('verifyOk')}</Badge>
                  ) : (
                    <Badge kind="bad">{t('verifyNg')}</Badge>
                  )}{' '}
                  <span className="faint">
                    ({t('verify')}: {s.totalsCount ?? '—'} {t('vehicles')} / {yen(s.totalsSum)})
                  </span>
                </p>
                <dl className="kv">
                  <dt>{t('added')}</dt>
                  <dd>{s.added}</dd>
                  <dt>{t('soldDiff')}</dt>
                  <dd style={{ color: s.sold > 0 ? 'var(--bad)' : undefined }}>{s.sold}</dd>
                  <dt>{t('returned')}</dt>
                  <dd>{s.returned}</dd>
                  <dt>{t('updated')}</dt>
                  <dd>{s.updated}</dd>
                  <dt>{t('unchanged')}</dt>
                  <dd className="muted">{s.unchanged}</dd>
                </dl>
              </Card>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <Card title={t('commit')}>
              <div className="filters">
                <Field label={t('snapshotDate')}>
                  <input type="date" value={snapshotDate} onChange={(e) => setSnapshotDate(e.target.value)} />
                </Field>
                <Field label={t('note')}>
                  <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </div>
              <p className="faint" style={{ marginBottom: 10 }}>
                {/* 売却日はこの基準日で記録される。 */}
                {t('soldDate')} = {t('snapshotDate')}
              </p>
              <button type="button" className="btn primary" disabled={busy} onClick={commit}>
                {busy ? t('committing') : t('commit')}
              </button>
            </Card>
          </div>

          <div style={{ marginTop: 14 }}>
            <div className="row" style={{ marginBottom: 10 }}>
              <Tabs
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'added', label: `${t('added')} (${totals?.added ?? 0})` },
                  { value: 'sold', label: `${t('soldDiff')} (${totals?.sold ?? 0})` },
                  { value: 'returned', label: `${t('returned')} (${totals?.returned ?? 0})` },
                  { value: 'updated', label: `${t('updated')} (${totals?.updated ?? 0})` },
                ]}
              />
            </div>

            <div className="table-wrap">
              {entriesByKind[tab].length === 0 ? (
                <Empty>{t('noResults')}</Empty>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>{t('division')}</th>
                      <th>{t('maker')}</th>
                      <th>{t('model')}</th>
                      <th>{t('grade')}</th>
                      <th>{t('chassis')}</th>
                      <th>{t('supplier')}</th>
                      <th className="num">{t('purchasePrice')}</th>
                      {tab === 'updated' && <th>{t('updated')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {entriesByKind[tab].map((e) => (
                      <tr key={`${e.division}-${e.chassisKey}`}>
                        <td>
                          <DivisionBadge division={e.division} />
                        </td>
                        <td>{e.maker}</td>
                        <td>{e.model || '—'}</td>
                        <td className="wrap muted">{e.grade || '—'}</td>
                        <td className="mono">{e.chassisRaw || '—'}</td>
                        <td>{e.supplier || '—'}</td>
                        <td className="num">{yen(e.purchasePrice)}</td>
                        {tab === 'updated' && (
                          <td className="wrap faint">
                            {Object.entries(e.changes ?? {})
                              .map(([field, [before, after]]) => `${field}: ${before || '—'} → ${after || '—'}`)
                              .join(' / ')}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
